import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoClose, IoArrowForward, IoArrowBack } from "react-icons/io5";
import { getPageConfig } from "@/lib/api";

const NERODUS_IMG = "/nerodus.png";

/**
 * Visite guidée menée par Nerodus. Déclenchée par l'événement window "td-start-tour".
 * Navigue page par page (route de chaque étape) et présente les sections.
 * Contenu éditable par l'admin via page-config "tour". Refaisable depuis les Paramètres.
 */
export default function GuidedTour() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [img, setImg] = useState(NERODUS_IMG);
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("step"); // step | outro | proposal

  useEffect(() => {
    const load = (goPhase) => {
      Promise.all([
        getPageConfig("tour").catch(() => null),
        getPageConfig("nerodus").then((n) => n?.image).catch(() => null),
      ]).then(([tour, nImg]) => {
        if (nImg) setImg(nImg);
        if (tour && (tour.steps || []).length) {
          setCfg(tour); setIdx(0); setPhase(goPhase); setActive(true);
        } else if (goPhase === "proposal") {
          nav("/hub");
        }
      });
    };
    const start = () => load("step");
    const offer = () => load("proposal");
    window.addEventListener("td-start-tour", start);
    window.addEventListener("td-offer-tour", offer);
    return () => { window.removeEventListener("td-start-tour", start); window.removeEventListener("td-offer-tour", offer); };
  }, [nav]);

  // Masquer la bulle Nerodus classique pendant la visite (flag global car NerodusGuide se remonte à chaque page).
  useEffect(() => {
    window.__tdTourActive = active;
    window.dispatchEvent(new CustomEvent("td-tour-active", { detail: active }));
  }, [active]);

  const steps = cfg?.steps || [];
  const cur = steps[idx];

  // Navigue vers la page de l'étape courante.
  useEffect(() => {
    if (active && phase === "step" && cur) nav(cur.route || "/hub");
    // eslint-disable-next-line
  }, [active, phase, idx]);

  if (!active || !cfg) return null;

  const finish = () => { setActive(false); };
  const goStep = (n) => setIdx(n);
  const next = () => { if (idx + 1 < steps.length) goStep(idx + 1); else setPhase("outro"); };
  const prev = () => { if (idx > 0) goStep(idx - 1); };
  const enterDojo = () => { nav("/hub"); finish(); };

  if (phase === "proposal") {
    return (
      <div style={s.backdrop} data-testid="tour-proposal">
        <div style={s.outroCard}>
          <div style={s.avatarBig}><img src={img} alt="Nerodus" style={s.avatarImg} /></div>
          <h2 style={s.outroTitle}>{cfg.intro_title || "Envie que je te fasse visiter ?"}</h2>
          <p style={s.outroText}>{cfg.intro_text}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button style={s.primary} onClick={() => { setIdx(0); setPhase("step"); }} data-testid="tour-accept">{cfg.cta_start || "Fais-moi visiter"} <IoArrowForward size={16} /></button>
            <button style={s.ghostWide} onClick={enterDojo} data-testid="tour-decline">{cfg.cta_skip || "J'entre directement"}</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "outro") {
    return (
      <div style={s.backdrop} data-testid="tour-outro">
        <div style={s.outroCard}>
          <div style={s.avatarBig}><img src={img} alt="Nerodus" style={s.avatarImg} /></div>
          <h2 style={s.outroTitle}>{cfg.outro_title || "Voilà, tu connais les lieux."}</h2>
          <p style={s.outroText}>{cfg.outro_text}</p>
          <button style={s.primary} onClick={enterDojo} data-testid="tour-enter">{cfg.outro_cta || "Entrer dans le Dōjō"} <IoArrowForward size={17} /></button>
        </div>
      </div>
    );
  }

  const total = steps.length;
  return (
    <div style={s.wrap} className="gtour-wrap" data-testid="guided-tour">
      <div style={s.card}>
        <button style={s.close} onClick={finish} data-testid="tour-quit" title="Quitter la visite"><IoClose size={18} /></button>
        <div style={s.head}>
          <div style={s.avatar}><img src={img} alt="Nerodus" style={s.avatarImg} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={s.stepMeta}>Visite guidée · {idx + 1}/{total}</div>
            <div style={s.title}><span style={s.kanji}>{cur.kanji}</span> {cur.title}</div>
          </div>
        </div>
        <p style={s.text} data-testid="tour-text">{cur.text}</p>
        <div style={s.progress}><div style={{ ...s.progressFill, width: `${((idx + 1) / total) * 100}%` }} /></div>
        <div style={s.actions}>
          <button style={{ ...s.ghost, opacity: idx === 0 ? 0.4 : 1 }} onClick={prev} disabled={idx === 0} data-testid="tour-prev"><IoArrowBack size={15} /> Précédent</button>
          <button style={s.skip} onClick={finish} data-testid="tour-skip">Passer la visite</button>
          <button style={s.primary} onClick={next} data-testid="tour-next">{idx + 1 < total ? "Suivant" : "Terminer"} <IoArrowForward size={15} /></button>
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap: { position: "fixed", left: 0, right: 0, bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)", zIndex: 10000, display: "flex", justifyContent: "center", padding: "0 14px", pointerEvents: "none" },
  card: { pointerEvents: "auto", position: "relative", width: "min(560px, 100%)", background: "var(--bg-secondary)", border: "1px solid var(--border-c)", borderRadius: 20, boxShadow: "0 24px 70px rgba(0,0,0,0.45)", padding: "18px 20px 16px", animation: "nerodusIn .3s cubic-bezier(.16,1,.3,1)" },
  close: { position: "absolute", top: 12, right: 12, width: 30, height: 30, borderRadius: 9, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10, paddingRight: 30 },
  avatar: { width: 52, height: 52, borderRadius: "50%", overflow: "hidden", border: "2px solid var(--brand)", flexShrink: 0, background: "var(--bg-primary)" },
  avatarBig: { width: 120, height: 120, borderRadius: "50%", overflow: "hidden", border: "3px solid var(--brand)", boxShadow: "0 0 45px color-mix(in srgb, var(--brand) 40%, transparent)", margin: "0 auto 6px", background: "var(--bg-primary)" },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" },
  stepMeta: { fontSize: 11.5, fontWeight: 800, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 0.6 },
  title: { fontSize: 19, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", display: "flex", alignItems: "center", gap: 8, marginTop: 2 },
  kanji: { fontSize: 20, color: "var(--brand)" },
  text: { fontSize: 14.5, lineHeight: 1.6, color: "var(--text-primary)", margin: "0 0 14px" },
  progress: { height: 5, background: "var(--border-c)", borderRadius: 999, overflow: "hidden", marginBottom: 14 },
  progressFill: { height: "100%", background: "var(--brand)", transition: "width .3s ease" },
  actions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "space-between" },
  ghost: { display: "inline-flex", alignItems: "center", gap: 5, padding: "9px 14px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-secondary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  skip: { border: "none", background: "transparent", color: "var(--text-secondary)", fontSize: 13, fontWeight: 700, textDecoration: "underline", cursor: "pointer" },
  primary: { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", borderRadius: 10, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 6px 18px color-mix(in srgb, var(--brand) 35%, transparent)" },
  backdrop: { position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  outroCard: { width: "min(460px, 100%)", background: "var(--bg-secondary)", border: "1px solid var(--border-c)", borderRadius: 22, padding: "32px 28px", textAlign: "center", boxShadow: "0 30px 80px rgba(0,0,0,0.5)", animation: "nerodusIn .3s cubic-bezier(.16,1,.3,1)" },
  outroTitle: { fontSize: 24, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: "0 0 8px" },
  outroText: { fontSize: 15, lineHeight: 1.65, color: "var(--text-secondary)", margin: "0 0 20px" },
  ghostWide: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-secondary)", fontWeight: 700, fontSize: 14, cursor: "pointer", width: "100%" },
};
